import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const GOOGLE_MAPS_POLL_INTERVAL_MS = 150;
const GOOGLE_MAPS_MAX_ATTEMPTS = 40;

/** @returns {Promise<typeof google.maps>} */
function waitForGoogleMaps() {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    (function poll() {
      if (window.google?.maps) {
        resolve(window.google.maps);
        return;
      }

      if (++attempts > GOOGLE_MAPS_MAX_ATTEMPTS) {
        reject(new Error('Google Maps failed to load. Check the API key, billing, and authorized domains.'));
        return;
      }

      setTimeout(poll, GOOGLE_MAPS_POLL_INTERVAL_MS);
    })();
  });
}

/** @param {string} value */
function normalize(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Great-circle distance in km (haversine).
 *
 * @param {{ lat: number, lng: number }} a
 * @param {{ lat: number, lng: number }} b
 */
function distanceKm(a, b) {
  const toRad = (/** @type {number} */ deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;

  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );
}

/**
 * @typedef {Object} StoreCategory
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} Store
 * @property {string} id
 * @property {string} name
 * @property {string} [description]
 * @property {string} [address]
 * @property {string} [postal_code]
 * @property {string} [city]
 * @property {string} [website]
 * @property {string | null} [logo]
 * @property {number} latitude
 * @property {number} longitude
 * @property {string[]} category_ids
 */

/**
 * Fetches store + category data from a metaobject-backed JSON page and renders
 * a filterable Google Map with a matching store list.
 *
 * @typedef {{ chips: HTMLElement, search: HTMLInputElement, list: HTMLElement, dots: HTMLElement, map: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class StoreLocator extends Component {
  requiredRefs = ['chips', 'search', 'list', 'dots', 'map'];

  /** @type {Store[]} */
  #stores = [];
  /** @type {StoreCategory[]} */
  #categories = [];
  /** @type {Set<string>} */
  #activeCategoryIds = new Set();
  /** @type {google.maps.Map | null} */
  #map = null;
  /** @type {google.maps.InfoWindow | null} */
  #infoWindow = null;
  /** @type {Map<string, google.maps.Marker>} */
  #markersByStoreId = new Map();
  /** @type {string | null} */
  #selectedStoreId = null;
  /** @type {{ lat: number, lng: number } | null} */
  #userPosition = null;
  /** Location of the searched postal code / place; takes priority over #userPosition for sorting. */
  /** @type {{ lat: number, lng: number } | null} */
  #searchPoint = null;
  /** Text filter, only used when a search can't be resolved to a location. */
  #textQuery = '';
  /** @type {google.maps.places.PlacesService | null} */
  #placesService = null;
  /** @type {google.maps.LatLngBounds | undefined} */
  #storeBounds = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.refs.list.addEventListener('scroll', this.#onListScroll);
    this.#boot();
  }

  async #boot() {
    try {
      const [maps] = await Promise.all([waitForGoogleMaps(), this.#loadData()]);

      this.#initMap(maps);
      this.#renderChips();
      this.#applyFilters();
      this.#locateUser();
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Asks the browser for the visitor's location and, if granted, re-sorts the
   * list by proximity. If denied or unavailable, the default order is kept.
   */
  #locateUser() {
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        this.#userPosition = { lat: coords.latitude, lng: coords.longitude };
        this.#applyFilters();
      },
      () => {},
      { maximumAge: 10 * 60 * 1000, timeout: 10000 }
    );
  }

  async #loadData() {
    const url = this.getAttribute('data-stores-url');
    if (!url) throw new Error('Missing data-stores-url attribute.');

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Could not load store data (HTTP ${response.status}).`);

    const json = await response.json();

    this.#categories = Array.isArray(json.categories) ? json.categories : [];
    this.#stores = (Array.isArray(json.stores) ? json.stores : []).filter(
      (store) => Number.isFinite(store.latitude) && Number.isFinite(store.longitude)
    );
  }

  /** @param {typeof google.maps} maps */
  #initMap(maps) {
    this.#map = new maps.Map(this.refs.map, {
      center: {
        lat: Number(this.getAttribute('data-default-lat')) || 41.38879,
        lng: Number(this.getAttribute('data-default-lng')) || 2.15899,
      },
      zoom: Number(this.getAttribute('data-default-zoom')) || 7,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    this.#infoWindow = new maps.InfoWindow();

    if (this.#stores.length) {
      this.#storeBounds = new maps.LatLngBounds();
      for (const store of this.#stores) this.#storeBounds.extend({ lat: store.latitude, lng: store.longitude });
    }

    if (maps.places) {
      this.#placesService = new maps.places.PlacesService(this.#map);

      // Suggest postal codes, cities, streets and addresses (not businesses — our own
      // store names are matched in #searchByText), biased towards the stores' area
      const autocomplete = new maps.places.Autocomplete(this.refs.search, {
        types: ['geocode'],
        fields: ['geometry'],
        bounds: this.#storeBounds,
      });
      autocomplete.addListener('place_changed', () => {
        const location = autocomplete.getPlace().geometry?.location;

        // Enter without picking a suggestion returns a place with no geometry
        if (location) {
          this.#setSearchPoint(location);
        } else {
          this.#searchByText(this.refs.search.value.trim());
        }
      });
    }
  }

  /**
   * Matches free text against our store names first; otherwise resolves it
   * (e.g. "08003", a street) to a location and sorts stores by proximity to it.
   * Falls back to a plain text filter if nothing is found.
   *
   * @param {string} query
   */
  #searchByText(query) {
    if (!query) {
      this.#clearSearch();
      return;
    }

    const MIN_STORE_NAME_QUERY_LENGTH = 3;
    const normalizedQuery = normalize(query);
    const nameMatches =
      normalizedQuery.length >= MIN_STORE_NAME_QUERY_LENGTH
        ? this.#stores.filter((store) => normalize(store.name).includes(normalizedQuery))
        : [];

    // One store matches: sort from it and open it on the map
    const [onlyMatch] = nameMatches;
    if (onlyMatch && nameMatches.length === 1) {
      this.#searchPoint = { lat: onlyMatch.latitude, lng: onlyMatch.longitude };
      this.#textQuery = '';
      this.#applyFilters();
      this.#selectStore(onlyMatch);
      return;
    }

    // Several stores match (e.g. "Casa"): show just those
    if (nameMatches.length > 1) {
      this.#searchPoint = null;
      this.#textQuery = normalizedQuery;
      this.#applyFilters();
      return;
    }

    const fallbackToText = () => {
      this.#searchPoint = null;
      this.#textQuery = normalize(query);
      this.#applyFilters();
    };

    if (!this.#placesService) {
      fallbackToText();
      return;
    }

    this.#placesService.findPlaceFromQuery(
      { query, fields: ['geometry'], locationBias: this.#storeBounds },
      (results, status) => {
        const location = status === google.maps.places.PlacesServiceStatus.OK && results?.[0]?.geometry?.location;
        if (location) {
          this.#setSearchPoint(location);
        } else {
          fallbackToText();
        }
      }
    );
  }

  /** @param {google.maps.LatLng} location */
  #setSearchPoint(location) {
    this.#searchPoint = { lat: location.lat(), lng: location.lng() };
    this.#textQuery = '';
    this.#applyFilters();
  }

  #clearSearch() {
    this.#searchPoint = null;
    this.#textQuery = '';
    this.#applyFilters();
  }

  #renderChips() {
    this.refs.chips.innerHTML = '';

    for (const category of this.#categories) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'store-locator__chip';
      chip.textContent = category.name;
      chip.setAttribute('aria-pressed', 'false');
      chip.addEventListener('click', () => this.#toggleCategory(category.id, chip));
      this.refs.chips.appendChild(chip);
    }
  }

  /**
   * @param {string} categoryId
   * @param {HTMLButtonElement} chip
   */
  #toggleCategory(categoryId, chip) {
    const isActive = this.#activeCategoryIds.has(categoryId);

    chip.classList.toggle('is-active', !isActive);
    chip.setAttribute('aria-pressed', String(!isActive));

    if (isActive) {
      this.#activeCategoryIds.delete(categoryId);
    } else {
      this.#activeCategoryIds.add(categoryId);
    }

    this.#applyFilters();
  }

  // Searching happens on autocomplete selection / Enter / submit; typing only resets when cleared
  onSearchInput = debounce(() => {
    if (!this.refs.search.value.trim()) this.#clearSearch();
  }, 200);

  onSearchSubmit() {
    this.onSearchInput.cancel();
    this.#searchByText(this.refs.search.value.trim());
  }

  #applyFilters() {
    const query = this.#textQuery;
    const activeCategoryIds = this.#activeCategoryIds;

    const filtered = this.#stores.filter((store) => {
      const haystack = normalize(
        [store.name, store.address, store.postal_code, store.city].filter(Boolean).join(' ')
      );
      const matchesQuery = !query || haystack.includes(query);
      const matchesCategory =
        !activeCategoryIds.size || store.category_ids.some((id) => activeCategoryIds.has(id));

      return matchesQuery && matchesCategory;
    });

    const origin = this.#searchPoint ?? this.#userPosition;
    if (origin) {
      filtered.sort(
        (a, b) =>
          distanceKm(origin, { lat: a.latitude, lng: a.longitude }) -
          distanceKm(origin, { lat: b.latitude, lng: b.longitude })
      );
    }

    this.#renderMarkers(filtered);
    this.#renderList(filtered);
  }

  /** @param {Store[]} stores */
  #renderMarkers(stores) {
    if (!this.#map) return;

    this.#markersByStoreId.forEach((marker) => marker.setMap(null));
    this.#markersByStoreId.clear();

    const bounds = new google.maps.LatLngBounds();

    // After a search, frame the searched point and its nearest stores (list is already sorted)
    const NEAREST_STORES_TO_FRAME = 3;
    if (this.#searchPoint) bounds.extend(this.#searchPoint);

    stores.forEach((store, index) => {
      const position = { lat: store.latitude, lng: store.longitude };
      const marker = new google.maps.Marker({ position, map: this.#map, title: store.name });

      marker.addListener('click', () => this.#selectStore(store));

      this.#markersByStoreId.set(store.id, marker);
      if (!this.#searchPoint || index < NEAREST_STORES_TO_FRAME) bounds.extend(position);
    });

    if (stores.length) this.#map.fitBounds(bounds, 60);
  }

  /**
   * @param {Store} store
   * @param {google.maps.Marker} marker
   */
  #openInfoWindow(store, marker) {
    if (!this.#infoWindow || !this.#map) return;

    this.#infoWindow.setContent(
      store.logo
        ? `
      <div class="store-locator__popup store-locator__popup--logo">
        <img class="store-locator__popup-logo" src="${escapeHtml(store.logo)}" alt="${escapeHtml(store.name)}">
      </div>
    `
        : `
      <div class="store-locator__popup">
        <strong>${escapeHtml(store.name)}</strong><br>
        ${escapeHtml(store.address || '')}<br>
        ${escapeHtml([store.postal_code, store.city].filter(Boolean).join(' '))}
      </div>
    `
    );
    this.#infoWindow.open({ map: this.#map, anchor: marker });
  }

  /** @param {Store[]} stores */
  #renderList(stores) {
    this.refs.list.innerHTML = '';
    this.refs.list.scrollTop = 0;
    this.#renderDots(stores);

    if (!stores.length) {
      const emptyLabel = this.getAttribute('data-empty-label') || 'No results';
      this.refs.list.innerHTML = `<p class="store-locator__empty">${escapeHtml(emptyLabel)}</p>`;
      return;
    }

    const directionsLabel = this.getAttribute('data-directions-label') || 'Directions';
    const fragment = document.createDocumentFragment();

    for (const store of stores) {
      const card = document.createElement('article');
      card.className = `store-locator__card${store.id === this.#selectedStoreId ? ' is-selected' : ''}`;
      card.dataset.storeId = store.id;
      card.setAttribute('role', 'listitem');
      card.tabIndex = 0;
      card.innerHTML = `
        <h3 class="store-locator__card-title">${escapeHtml(store.name)}</h3>
        ${store.description ? `<p class="store-locator__card-description">${escapeHtml(store.description)}</p>` : ''}
        <p class="store-locator__card-address">
          ${escapeHtml(store.address || '')}<br>
          ${escapeHtml([store.postal_code, store.city].filter(Boolean).join(' '))}
        </p>
        ${
          store.website
            ? `<a class="store-locator__card-link" href="${escapeHtml(store.website)}" target="_blank" rel="nofollow noopener">${escapeHtml(store.website)}</a>`
            : ''
        }
        <span class="store-locator__card-spacer"></span>
        <a
          class="store-locator__card-directions"
          href="https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}"
          target="_blank"
          rel="nofollow noopener"
        >${escapeHtml(directionsLabel)}</a>
      `;

      card.addEventListener('click', () => this.#selectStore(store));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') this.#selectStore(store);
      });

      fragment.appendChild(card);
    }

    this.refs.list.appendChild(fragment);
    this.#setupLoop(stores.length);
  }

  /**
   * Loops the mobile carousel: prepends a clone of the last card and appends
   * a clone of the first, then starts the scroll position on the first real
   * card. `#onListScroll` silently snaps between a real edge card and its
   * clone once the user scrolls onto it, so swiping never hits a hard stop.
   *
   * @param {number} storeCount
   */
  #setupLoop(storeCount) {
    const { list } = this.refs;

    if (storeCount <= 1) {
      list.scrollLeft = 0;
      return;
    }

    const cards = list.children;
    const leadingClone = /** @type {HTMLElement} */ (cards[cards.length - 1].cloneNode(true));
    const trailingClone = /** @type {HTMLElement} */ (cards[0].cloneNode(true));

    for (const clone of [leadingClone, trailingClone]) {
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
    }

    list.prepend(leadingClone);
    list.appendChild(trailingClone);
    list.scrollLeft = list.clientWidth;
  }

  /** @param {Store[]} stores */
  #renderDots(stores) {
    this.refs.dots.innerHTML = '';

    if (stores.length <= 1) return;

    const fragment = document.createDocumentFragment();

    stores.forEach((store, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `store-locator__dot${index === 0 ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', store.name);
      dot.addEventListener('click', () => {
        this.refs.list.scrollTo({ left: (index + 1) * this.refs.list.clientWidth, behavior: 'smooth' });
      });
      fragment.appendChild(dot);
    });

    this.refs.dots.appendChild(fragment);
  }

  #onListScroll = debounce(() => {
    const { list, dots } = this.refs;
    const realCount = dots.children.length;
    if (!realCount || !list.clientWidth) return;

    let domIndex = Math.round(list.scrollLeft / list.clientWidth);

    if (domIndex <= 0) {
      domIndex = realCount;
      list.scrollLeft = list.clientWidth * domIndex;
    } else if (domIndex >= realCount + 1) {
      domIndex = 1;
      list.scrollLeft = list.clientWidth * domIndex;
    }

    const activeIndex = domIndex - 1;

    for (const [dotIndex, dot] of Array.from(dots.children).entries()) {
      dot.classList.toggle('is-active', dotIndex === activeIndex);
    }
  }, 100);

  /** @param {Store} store */
  #selectStore(store) {
    this.#selectedStoreId = store.id;
    this.#highlightSelectedCard();

    if (!this.#map) return;

    this.#map.panTo({ lat: store.latitude, lng: store.longitude });
    this.#map.setZoom(Math.max(this.#map.getZoom() ?? 0, 13));

    const marker = this.#markersByStoreId.get(store.id);
    if (marker) this.#openInfoWindow(store, marker);
  }

  #highlightSelectedCard() {
    for (const card of this.refs.list.children) {
      if (!(card instanceof HTMLElement)) continue;
      card.classList.toggle('is-selected', card.dataset.storeId === this.#selectedStoreId);
    }
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.map.innerHTML = `<div class="store-locator__error">${escapeHtml(message)}</div>`;
    console.error('[StoreLocator]', message);
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
